import { usePrevious } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { getPerformanceResultAvg } from './util';
import type { EditorPerformanceResult } from '@carboncopyco/types';

interface Props {
  isPerformanceStarted: boolean;
  performanceResults: EditorPerformanceResult;
  numberOfSeconds: number;
  numberOfIterations: number;
  shouldStopOnLoad: boolean;
  prevIsPerformanceStarted: boolean;
  onPerformanceResultsAvgCalculated: (result: EditorPerformanceResult) => void;
  onRerun(val: boolean);
}

export const usePerformanceIterations = ({
  isPerformanceStarted,
  performanceResults,
  numberOfSeconds,
  numberOfIterations,
  shouldStopOnLoad,
  prevIsPerformanceStarted,
  onPerformanceResultsAvgCalculated,
  onRerun,
}: Props) => {
  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>();

  const performanceResultsRef = useRef(performanceResults);
  const [performanceResultsForIterations, setPerformanceResultsForIterations] =
    useState([]);
  const [numberOfIterationsCompleted, setNumberOfIterationsCompleted] =
    useState(0);
  const [isAvgCalculating, setIsAvgCalculating] = useState(false);
  const prevIsAvgCalculating = usePrevious(isAvgCalculating);

  useEffect(() => {
    performanceResultsRef.current = performanceResults;
  }, [performanceResults]);

  useEffect(() => {
    if (prevIsAvgCalculating && !isAvgCalculating) {
      const avgPerformanceResults = getPerformanceResultAvg([
        ...performanceResultsForIterations,
        performanceResults,
      ]);
      onPerformanceResultsAvgCalculated(avgPerformanceResults);
    }
  }, [isAvgCalculating]);

  useEffect(() => {
    setTimeout(() => {
      if (prevIsPerformanceStarted && !isPerformanceStarted) {
        const newNumberOfIterationsCompleted = numberOfIterationsCompleted + 1;
        setNumberOfIterationsCompleted(newNumberOfIterationsCompleted);

        const newResults = [
          ...performanceResultsForIterations,
          performanceResultsRef.current,
        ];

        setPerformanceResultsForIterations(newResults);

        if (
          Number(newNumberOfIterationsCompleted) < Number(numberOfIterations)
        ) {
          setIsAvgCalculating(true);

          onRerun(true);

          if (Number(numberOfSeconds) > 0 && !shouldStopOnLoad) {
            timeoutRef.current = setTimeout(() => {
              onRerun(false);
            }, Number(numberOfSeconds) * 1000);
          }
        } else {
          setIsAvgCalculating(false);
        }
      }
    }, 500);
  }, [isPerformanceStarted]);

  return {
    setNumberOfIterationsCompleted,
    setPerformanceResultsForIterations,
    numberOfIterationsCompleted,
    timeoutRef,
  };
};
